import { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { SiteData, SiteUrls, useSite } from '@/services/site/useSite';
import { useCartContext } from '@/services/cart/CartContext';
import { useClient } from '@/services/client/useClient';
import {
  GocardlessExecuteRequest,
  GocardlessExecuteResponse,
  useGocardlessExecuteMutation,
} from '@/api/payments/gocardlessExecute';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { setSubscriptionId } from '@/features/paymentProcess/transactionSlice';
import { UserDetailsState } from '@/features/userDetails/userDetailsSlice';
import { ShippingDetailsState } from '@/features/shippingDetails/shippingDetailsSlice';
import { FULFILMENT_TYPES } from '@/constants/constants';

import { GocardlessState } from './gocardlessSlice';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';

const buildExecutePayload = (
  gocardlessState: GocardlessState,
  paymentSelectState: PaymentSelectState,
  userDetailsState: UserDetailsState,
  shippingDetailsState: ShippingDetailsState,
  siteData: SiteData,
  siteUrls: SiteUrls,
  client: string,
  campaignCode: string | null,
  isPayin: boolean,
  cartId?: string,
  isAARN?: boolean,
): GocardlessExecuteRequest => {
  const payload: GocardlessExecuteRequest = {
    firstName: userDetailsState.firstName,
    lastName: userDetailsState.lastName,
    email: userDetailsState.email,
    address1: userDetailsState.address1,
    address2: userDetailsState.address2,
    address3: userDetailsState.address3,
    town: userDetailsState.town,
    postcode: userDetailsState.postcode,
    country: userDetailsState.country,
    amount: paymentSelectState.amount,
    currency: paymentSelectState.currency,
    giftaid: Number(paymentSelectState.giftaid),
    order_reference: paymentSelectState.orderReference,
    accountHolderName: gocardlessState.isAccountHolder ? `${userDetailsState.firstName} ${userDetailsState.lastName}` : gocardlessState.accountHolderName,
    accountNumber: gocardlessState.accountNumber,
    billingDayOfMonth: gocardlessState.billingDay,
    sortCode: gocardlessState.sortCode,
    affiliate: null,
    campaign: campaignCode ?? siteData.campaign?.name,
    client,
    cartId: cartId ?? siteData.defaultCart,
    transSource: siteData.campaign.transSource,
    transSourceUrl: siteUrls.transSourceUrl,
    transType: isPayin ? 'payin' : siteData.campaign.transType,
  };

  // Add AARN metadata if this is an AARN cart
  if (isAARN && shippingDetailsState.postalRewards) {
    const shippingAddress = shippingDetailsState.useBillingAddress
      ? {
        address1: userDetailsState.address1,
        address2: userDetailsState.address2,
        address3: userDetailsState.address3,
        town: userDetailsState.town,
        postcode: userDetailsState.postcode,
        country: userDetailsState.country,
      }
      : {
        address1: shippingDetailsState.address1,
        address2: shippingDetailsState.address2,
        address3: shippingDetailsState.address3,
        town: shippingDetailsState.town,
        postcode: shippingDetailsState.postcode,
        country: shippingDetailsState.country,
      };

    // Build recipient name based on child name logic
    const recipient: { first_name: string; last_name?: string; address1: string; address2?: string; address3?: string; town: string; postcode: string; country: string } = {
      ...(
        shippingDetailsState.childName
          ? { first_name: shippingDetailsState.childName }
          : { first_name: userDetailsState.firstName, last_name: userDetailsState.lastName }
      ),
      address1: shippingAddress.address1,
      address2: shippingAddress.address2 ?? undefined,
      address3: shippingAddress.address3 ?? undefined,
      town: shippingAddress.town,
      postcode: shippingAddress.postcode,
      country: shippingAddress.country,
    };

    payload.metadata = JSON.stringify({
      fulfilment: {
        type: 'aarn',
        recipient,
      },
    });
  }

  return payload;
};

export function useGocardlessExecute() {
  const onSuccess = useRef<((data: GocardlessExecuteResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const gocardlessState = useAppSelector(state => state.gocardless);
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const userDetailsState = useAppSelector(state => state.userDetails);
  const shippingDetailsState = useAppSelector(state => state.shippingDetails);
  const { cartId, getCartFulfilmentType, getCartFinancialAttribution } = useCartContext();

  const { getClientId } = useClient();
  const { getSiteData, getSiteUrls } = useSite();
  const dispatch = useAppDispatch();

  const { selectedJourney } = useJourneyOptions();
  const isPayin = selectedJourney === 'payin';

  const [gocardlessExecuteTrigger, {
    isLoading,
    data,
    error,
  }] = useGocardlessExecuteMutation();

  // Trigger payment-service execute
  const triggerExecuteApi = useCallback(async (
    callbacks?: {
      onSuccess?: (data: GocardlessExecuteResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    try {
      const isAARN = getCartFulfilmentType() === FULFILMENT_TYPES.AARN;

      const payload = buildExecutePayload(
        gocardlessState,
        paymentSelectState,
        userDetailsState,
        shippingDetailsState,
        getSiteData(),
        getSiteUrls(),
        getClientId(),
        getCartFinancialAttribution()?.campaignCode ?? null,
        isPayin,
        cartId,
        isAARN,
      );

      await gocardlessExecuteTrigger(payload);
    } catch (err) {
      logger.error('Error executing');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }
  }, [getClientId, getSiteData, getSiteUrls, gocardlessExecuteTrigger, gocardlessState, paymentSelectState, userDetailsState, shippingDetailsState, getCartFinancialAttribution, isPayin, cartId, getCartFulfilmentType]);

  useEffect(() => {
    // Wait until payment-service is complete
    if (isLoading || (!data && !error)) {
      return;
    }

    // Handle payment-service errors
    if (!!error || data?.message !== 'success' || !data?.data?.subscriptionId) {
      logger.error('Error executing');
      logger.error(error);

      if (onError.current) {
        onError.current(error);
      }
      return;
    }

    if (onSuccess.current) {
      onSuccess.current(data);
      dispatch(setSubscriptionId(data.data.subscriptionId));
    }
  }, [isLoading, data, error, onSuccess, onError, dispatch]);

  return {
    triggerExecuteApi,
    executeIsLoading: isLoading,
    executeData: data,
    executeError: error,
  };
}
